import {type ErrorMap, errorMapKey, injectErrors, injectFormData, injectInterfaceRoot, injectLib, pathKey, provideWidgetDisplay} from "./PartInjectKeys";
import {computed, type ComputedRef, inject, provide, ref, type Ref} from "vue";
import get from "lodash.get";
import set from "lodash.set";
import {injectEnv} from "./Environment";
import type {FormDefinition, FormMethodCompiled} from "@t/fd/layout/FormDefinition";
import type {FormPart, FormPartOptions} from "@t/fd/FormPart";
import type {LibraryFunctions} from "@t/fd/Lib";
import type {ApiError} from "@t/util/ApiError";
import type {FormData} from "@t/fd/FormPart";
import {injectMaskingLib} from "@fd/Masking.ts";
import {MaskingTest} from "@t/fd/layout/Masked.ts";
import {isSecured, ObjectAccess, ObjectAccessFlag, secureRootKey} from "@t/security/ObjectAccess.ts";
import {InterfaceRoot, RootLayout} from "@t/fd/InterfaceRoot.ts";

/**
 * Overview of code parameters:
 * g - points to the root of data object
 * o - points to a local data (may or may not be the same as g)
 * f - points to UI root (agenda, form or other component)
 * p - points lo local part (the part doing the execution)
 * r - points to interface root (InterfaceRoot "interface")
 * lib - library of helper functions
 * @param code source code to be compiled
 */
function compileCode(code: string): FormMethodCompiled {
	const params = ["g", "o", "f", "p", "r", "lib"];
    const body = "return (" + code + ");";
    params.push(body); // function body
	return new Function(...params) as FormMethodCompiled;
}

function evaluateCode(g: any, o: any, r: InterfaceRoot<RootLayout>, p: FormPart, fn: FormMethodCompiled, lib:LibraryFunctions) {
	const f = r.content;
	return fn(g, o, f, p, r, lib);
}

function compileAndEvaluate(codeObject: any, codeProperty: string|number, g: any, o: any, r: InterfaceRoot<RootLayout>, p: FormPart, lib:LibraryFunctions) {
	let code = codeObject[codeProperty];
	if (typeof code === "string") {
		code = compileCode(code);
		codeObject[codeProperty] = code; // store compiled
	}
	return evaluateCode(g, o, r, p, code, lib);
}

const NO_ERRORS:ApiError[] = [];

export function getter(data: FormData, index: string | number | null | undefined, globalData?:FormData): any {
    if (index == null) {
        return data;
    }
    if (Array.isArray(data)) {
        const numericIndex = typeof index === "string" ? parseInt(index) : index;
        return data[numericIndex];
    } else {
        const propName = typeof index === "string" ? index : index.toString();
        if (propName.startsWith('.')) return get(globalData, propName.substring(1));
        return get(data, propName);
    }
}

export function setter(data: FormData, index: string | number | null | undefined, value:any, globalData?:FormData):void {
    if (index == null) {
        console.warn("Attempt to set data without index or property name:\nData: "+JSON.stringify(data)+"\nValue: "+JSON.stringify(value));
    } else {
        if (Array.isArray(data)) {
            const numericIndex = typeof index === "string" ? parseInt(index) : index;
            data[numericIndex] = value;
        } else {
            const propName = typeof index === "string" ? index : index.toString();
            if (propName.startsWith('.') && globalData) set(globalData, propName.substring(1), value);
            set(data, propName, value);
        }
    }
}

function appendToPathNotNull(parent:string, property:string|number):string {
    if (parent.length > 0) return parent + "." + property;
    return property.toString();
}

function appendToPath(parent:string, property:string|number|undefined|null):string {
    if (property != null) return appendToPathNotNull(parent, property);
    return parent;
}

function arrayAppend<T>(arr: T[], toAppend: T[]) {
    if (toAppend === undefined) return;
    for (let a of toAppend) arr.push(a);
}

type PathConvert = (p:string) => string;

export function matchError(err:ApiError, path:string, toAbsolute:PathConvert):boolean {
    // console.log(" matching "+path+" against err " + err.field);
    if (path === ".") {
        // Global errors
        return err.field === undefined || err.field === "";
    } else if (path === "*" || path.length === 0) {
        // All errors
        return true;
    } else {
        if (err.field === undefined) return false;
        let testFor:string;
        if (path.startsWith('.')) testFor = path.substring(1);
        else testFor = toAbsolute(path);
        //console.log(" testFor = " + testFor);
        if (testFor.endsWith("*")) {
            // Prefix search
            testFor = testFor.substring(0, testFor.length - 1);
            return err.field.startsWith(testFor);
        } else {
            // Exact search
            return err.field === testFor;
        }
    }
}


function filterErrorsByPathToArray(errorMap:Ref<ErrorMap>, contextPath:string, fullPath:string, catchPath:string, filtered:ApiError[]):void {
    for (let err of errorMap.value) {
        if (matchError(err, catchPath, (relative:string) => appendToPath(contextPath, relative))) filtered.push(err);
    }
}

function filterErrorsByPath(errorMap:Ref<ErrorMap>, contextPath:string, fullPath:string, catchPath:string):ApiError[] {
    //console.log("Finding errors for "+catchPath+" in\n"+JSON.stringify(errorMap)+"\n contextPath="+contextPath+"\n fullPath="+fullPath);
    const filtered:ApiError[] = [];
    filterErrorsByPathToArray(errorMap, contextPath, fullPath, catchPath, filtered);
    //console.log("...found " + JSON.stringify(filtered));
    return filtered;
}

export interface PartProps {
	part: FormPart;
	data: FormData;
	readonly?: boolean | null;
	viewmode?: boolean | null;
	/* Widgets part announces to its children that they are direct children of Widgets part (can use subgrid) */
	insideWidgets?: boolean;
}

export const partProps = {
	part: {
		type: Object,
		required: true,
	},

	data: {
		type: [Object, Array],
		required: true,
	},

	/* This prop is contained in part object but can be inherited a for this reason they are also here */
	readonly: {
		type: Boolean,
		default: false,
	},

	viewmode: {
		type: Boolean,
		default: false,
	},

	insideWidgets: {
		type: Boolean,
		default: false,
	}
};

export function errorsFor(errorMap:Ref<ErrorMap>, part:FormPart, contextPath:string) {
    if (part.data != null) contextPath = appendToPath(contextPath, part.data);
    let path = contextPath;
    if (part.property != null) path = appendToPath(path, part.property);

    if (part.catch === undefined) return NO_ERRORS;
    if (typeof part.catch === "string") {
        return filterErrorsByPath(errorMap, contextPath, path, part.catch);
    } else if (Array.isArray(part.catch)) {
        const filtered:ApiError[] = [];
        for (let c of part.catch) {
            filterErrorsByPathToArray(errorMap, contextPath, path, c, filtered);
        }
        return filtered;
    }
    return NO_ERRORS;
}


export default function (props: PartProps, root?: ComputedRef<InterfaceRoot<RootLayout>>, form_data?: object | any[], form_lib?: any, errs?:ErrorMap) {

    let contextPath = inject(pathKey, "");
    if (props.part.data != null) {
		contextPath = appendToPath(contextPath, props.part.data);
		// New data root may be new secure (ObjectAccess) root
		const secureRoot = getter(props.data, props.part.data);
		if (isSecured(secureRoot)) {
			provide(secureRootKey, secureRoot);
		}
	}
    let path = contextPath;
    if (props.part.property != null) path = appendToPath(path, props.part.property);
    provide(pathKey, path);

	let errorMap:Ref<ErrorMap>;
	if (errs) errorMap = ref(errs);
	else errorMap = injectErrors();

	/** Part representing the whole interface */
	const rootPart: ComputedRef<InterfaceRoot<RootLayout>> = root ? root : injectInterfaceRoot();
	/** Content part of the interface (it may be form, agenda or other) */
	const uiPart = computed<RootLayout>(() => rootPart.value.content);

	const formData = form_data ? form_data : injectFormData();
    const lib = form_lib ? form_lib : injectLib();

	/** Property to index data */
	const prop = computed<string|number|undefined>(() => {
		return props.part.property;
	});

    /** Data collection (array or object) indexed by prop */
    const localData = computed<any>(() => {
        if (props.part.data != null) return getter(props.data, props.part.data, formData);
        else return props.data;
    });

    /** Options object */
	const options = computed<FormPartOptions>(() => {
		return props.part.options || {};
	});

	const clazz = computed(() => {
		return option("clazz", []);
	});

	const style = computed(() => {
		return option("style", {});
	});

	function findErrors(catching:undefined|null|string|string[]):ApiError[] {
		if (catching === undefined) return NO_ERRORS;
		if (typeof catching === "string") {
			return filterErrorsByPath(errorMap, contextPath, path, catching);
		} else if (Array.isArray(catching)) {
			const filtered:ApiError[] = [];
			for (let c of catching) {
				filterErrorsByPathToArray(errorMap, contextPath, path, c, filtered);
			}
			return filtered;
		}
		return NO_ERRORS;
	}

    const errors = computed<ApiError[]>(() => {
		return findErrors(props.part.catch);
    });

	function maxErrorLevelFor(include:(field?:string)=>boolean = (_) => true):string {
		let max = 0;
		for (let err of errors.value) {
			if (include(err.field)) {
				let code = 0;
				switch (err.level) {
					case "INFO":
						code = 1;
						break;
					case "WARNING":
						code = 2;
						break;
					case "SEVERE":
						code = 3;
				}
				if (code > max) max = code;
			}
		}
		if (max === 3) return "SEVERE";
		else if (max === 2) return "WARNING";
		else if (max === 1) return "INFO";
		return "";
	}

	function maxErrorLevelFields(...fields:string[]):string {
		return maxErrorLevelFor((field) => fields.includes(field));
	}

    const maxErrorLevel = computed<string>(() => {
		return maxErrorLevelFor(); /* all */
    });

	const errorMessage = computed<string>(() => {
		let max = 0;
		let maxError: ApiError = null;
		for (let err of errors.value) {
			let code = 0;
			switch (err.level) {
				case "INFO":
					code = 1;
					break;
				case "WARNING":
					code = 2;
					break;
				case "SEVERE":
					code = 3;
			}
			if (code > max) {
				max = code;
				maxError = err;
			}
		}
		if (maxError != null) return maxError.message;
		return null;
	});

    const errorClass = computed<string>(() => {
        const maxLevel = maxErrorLevel.value;
        if (maxLevel === "SEVERE") return "invalid";
        else if (maxLevel === "WARNING") return "warn";
        else if (maxLevel === "INFO") return "info-note";
        return "";
    });

	function errorClassFields(...fields:string[]):string {
		const maxLevel = maxErrorLevelFields(...fields);
		if (maxLevel === "SEVERE") return "invalid";
		else if (maxLevel === "WARNING") return "warn";
		else if (maxLevel === "INFO") return "info-note";
		return "";
	}

	/* Calculated view mode based on part and inherited prop */
	const vm = computed<boolean>((): boolean => {
		if (typeof props.part.viewMode === "undefined") {
			/* This part has not viewMode specified ... so it is inherited through viewmode property */
			if (props.viewmode == null) return false;
			return props.viewmode;
		}
		return props.part.viewMode;
	});


	function evalReadOnly(codeObject: any, codeProperty: string|number):boolean {
		const ro = codeObject[codeProperty];
		if (Array.isArray(ro)) {
			for (let i = 0; i < ro.length; i++) {
				if (evalReadOnly(ro, i)) return true;
			}
			return false;
		} else {
			let result:boolean|null = null;
			switch (typeof ro) {
				case "function":
					result = evaluateCode(formData, localData.value, rootPart.value, props.part, ro, lib);
					break;
				case "boolean":
					result = ro;
					break;
				case "string":
					result = compileAndEvaluate(codeObject, codeProperty, formData, localData.value, rootPart.value, props.part, lib);
					break;
				case "object":
					// Is masking test
					const mlib = injectMaskingLib();
					result = mlib.evaluateTest(localData.value, vm.value, ro as MaskingTest);
					break;
			}
			return result;
		}
	}

	function hasEditAccess(required:ObjectAccessFlag[]):boolean {
		if (isSecured(formData)) {
			const userAccess = formData._access;
			for (let flag of required) {
				if (userAccess[flag]) return true;
			}
			return false;
		}
		return false;
	}

	/* Calculated read only for this part based on part object, prop and viewmode */
	const ro = computed<boolean>((): boolean => {
		if (vm.value) return true; // if viewMode is true, we are always readonly
		if (props.part.editAccess && !hasEditAccess(props.part.editAccess)) return true;
		if (typeof props.part.readOnly === "undefined") {
			// missing in part
			return props.readonly ?? false;
		} else {
			const evaluatedRO = evalReadOnly(props.part, "readOnly");
			if (evaluatedRO == null) return props.readonly ?? false; // Inherited value should be used
			return evaluatedRO;
		}
	});

    function visibilityEval(codeObject: any, codeProperty: string | number):boolean {
        const code = codeObject[codeProperty];
		if (Array.isArray(code)) {
			for (let i = 0; i < code.length; i++) {
				if (!visibilityEval(code, i)) {
					return false;
				}
			}
			return true;
		} else {
			switch (typeof code) {
				case "function":
					return evaluateCode(formData, localData.value, rootPart.value, props.part, code, lib);
				case "boolean":
					return code;
				case "string":
					return compileAndEvaluate(codeObject, codeProperty, formData, localData.value, rootPart.value, props.part, lib);
				case "undefined":
					return true;
				case "object":
					// Is masking test
					const mlib = injectMaskingLib();
					const result = mlib.evaluateTest(localData.value, vm.value, code as MaskingTest);
					return result;
				default:
					return !!code;
			}
		}
    }


	const visible = computed<boolean>(() => {
        return visibilityEval(props.part, "visible");
	});

	function hasOption(name: string): boolean {
		if (options.value) {
			return typeof options.value[name] !== "undefined";
		}
		return false;
	}

	function option(name: string, defaultValue: any): any {
		if (options.value) {
			const val = options.value[name];
			if (val == null) return defaultValue;
			return val;
		}
		return defaultValue;
	}

    function valueExpression(valueProp:string, expressionProp:string):ComputedRef<string> {
        return computed<string>(() => {
            switch (typeof props.part[expressionProp]) {
                case "function":
                    return evaluateCode(formData, localData.value, rootPart.value, props.part, props.part[expressionProp], lib);
                case "string":
                    return compileAndEvaluate(props.part, expressionProp, formData, localData.value, rootPart.value, props.part, lib);
            }
            return props.part[valueProp];
        });
    }

	/**
	 * One time expression evaluation
	 */
	function evalExpression(expressionProp:string, expressionObject?:object):any {
		const obj = expressionObject || props.part;
		switch (typeof obj[expressionProp]) {
			case "function":
				return evaluateCode(formData, localData.value, rootPart.value, props.part, obj[expressionProp], lib);
			case "string":
				return compileAndEvaluate(obj, expressionProp, formData, localData.value, rootPart.value, props.part, lib);
		}
		return obj[expressionProp];
	}

    const env = injectEnv();
	const myDisplayRule = provideWidgetDisplay(props.part, env);

	return {
		// Computed
		prop,
		rootPart,	/* Root part*/
		uiPart, 	/* UI part */
		formData 	/* root data */,
        localData 	/* local data */,
		options,
		clazz,
		style,
		/** ApiError[] list of all errors catched by this component */
        errors,
		/** List errors matching provided catching paths */
		findErrors,
        errorLevel : maxErrorLevel,
        errorClass,
		errorClassFields,
		errorMessage,
        path,

		vm,
		ro,
		visible,
        visibilityEval,

		hasOption,
		option,
        valueExpression,
		evalExpression,
		myDisplayRule
	};
}
